/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmptyComponent } from './empty/empty.component';
import { OverviewPanelComponent } from './overview-panel/overview-panel.component';
import { CollapsibleSidenavComponent } from './collapsible-sidenav/collapsible-sidenav.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CountryCodeToTimezonePipe } from './country-code-to-timezone.pipe';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MenuItemHasAnyRightsPipe } from './menu-item-has-any-rights/menu-item-has-any-rights.pipe';
import { SaveAsPdfComponent } from './save-as-pdf/save-as-pdf.component';
import { WizardComponent } from './wizard/wizard.component';
import { WizardTabComponent } from './wizard/wizard-tab/wizard-tab.component';
import { EmptyListPlaceholderComponent } from './empty-list-placeholder/empty-list-placeholder.component';
import { PageTitleComponent } from './page-title/page-title.component';
import { FormControlErrorsComponent } from './form/form-control-errors/form-control-errors.component';
import { LinkComponent } from './link/link.component';
import { TimeAvailabilityComponent } from './time/time-availability/time-availability.component';
import { AvailabilityTimerComponent } from './time/availability-timer/availability-timer.component';
import { DateTimeDropdownPickerComponent } from './time/date-time-dropdown-picker/date-time-dropdown-picker.component';
import { PlaceAutocompleteFloatingLabelComponent } from './place-autocomplete-floating-label/place-autocomplete-floating-label.component';
import { DisplayTimeWindowComponent } from './display-time-window/display-time-window.component';
import { SelectTimeWindowComponent } from './select-time-window/select-time-window.component';
import { WellDatePipe } from './well-date-pipe/well-date.pipe';
import { SidebarMenuComponent } from './sidebar-menu/sidebar-menu.component';
import { MenuItemComponent } from './sidebar-menu/menu-item/menu-item.component';
import { MenuItemContentComponent } from './sidebar-menu/menu-item/menu-item-content/menu-item-content.component';
import { TooltipDropdownComponent } from './tooltip-dropdown/tooltip-dropdown.component';
import { ResourceInputComponent } from './resource-input/resource-input.component';
import { TimeUntilDeadlinePipe } from './time-until-deadline/time-until-deadline.pipe';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Popover } from 'primeng/popover';
import { FlagLanguageDropdownComponent } from './flag-language-dropdown/flag-language-dropdown.component';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DateUtilsModule } from '@irian-ro/date-utils';
import { TermsAndConditionsPreviewDialogComponent } from './terms-and-conditions-preview-dialog/terms-and-conditions-preview-dialog.component';
import { DialogModule } from 'primeng/dialog';
import { SharedComponentsModule as IriroSharedComponentsModule } from '@irian-ro/shared-components-module';
import { CompanyDetailsComponent } from './company-details/company-details.component';
import { WizardTabFooterDirective } from './wizard/wizard-tab-footer.directive';
import { MenuUserProfileComponent } from './collapsible-sidenav/menu-user-profile/menu-user-profile.component';
import { ListFilterComponent } from './list-filter/list-filter.component';
import { DisplayNullValuePipe } from './display-null-value-pipe/display-null-value.pipe';
import { StatusTagComponent } from './status-tag/status-tag.component';
import { SuggestionMessageComponent } from './suggestion-message/suggestion-message.component';
import { MessagesModule } from 'primeng/messages';
import { TimeTableFilterComponent } from './filters/time-table-filter/time-table-filter.component';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { StepWizardDialogComponent } from './step-wizard-dialog/step-wizard-dialog.component';
import { StepsModule } from 'primeng/steps';
import { StepWizardTabComponent } from './step-wizard-dialog/step-wizard-tab/step-wizard-tab.component';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { AlertNotificationCardComponent } from './alert-notification-card/alert-notification-card.component';
import { IsTimeAfterNowPipe } from './is-time-after-now-pipe/is-time-after-now.pipe';
import { CollapsiblePanelComponent } from './collapsible-panel/collapsible-panel.component';
import { CollapsibleTextComponent } from './collapsible-text/collapsible-text.component';
import { WellStringToDatePipe } from './pipes/well-string-to-date-pipe/well-string-to-date.pipe';
import { CountryCodeToLabelPipe } from './pipes/country-code-to-label/country-code-to-label.pipe';
import { ResourceSavedNavigationDialogComponent } from './resource-saved-navigation-dialog/resource-saved-navigation-dialog.component';
import { TranslateMenuItemsPipe } from './pipes/translate-menu-items/translate-menu-items.pipe';
import { ValidToFilterComponent } from './valid-to-filter/valid-to-filter.component';
import { TruncateSeriesNumberPipe } from './order-number-correction/truncate-series-number.pipe';
import { SelectableCardComponent } from './selectable-card/selectable-card.component';
import { HorizontalScrollComponent } from './horizontal-scroll/horizontal-scroll.component';
import { FormatVolumePipe } from './pipes/format-volume.pipe';
import { FileStorageModule } from '@well/file-storage';
import { AccountFieldComponent } from './account-field/account-field.component';
import { AccountDialogComponent } from './account-dialog/account-dialog.component';
import { AccountDropdownComponent } from './account-dropdown/account-dropdown.component';
import { ResourceDropdownModule } from '@well/resource-dropdown';
import { ListboxModule } from 'primeng/listbox';
import { GetFirstThreeChrPipe } from './pipes/get-first-three-chr.pipe';
import { LanguageDialogComponent } from './language-dialog/language-dialog.component';
import { RippleComponent } from './ripple-component/ripple.component';
import { PreventScrollOnRouterOutletDirective } from './directives/prevent-scroll-on-router-outlet/prevent-scroll-on-router-outlet.directive';
import { WarningTextComponent } from './warning-text/warning-text.component';
import { GlobalFilterComponent } from './global-filter/global-filter.component';
import { DistanceAsyncValidator } from './validators/distance-async.validator';
import { PersonCircleComponent } from './person-circle/person-circle.component';
import { ViewOnlyDirective } from './view-only/view-only.directive';
import { AbstractContactListComponent } from './abstract-contact-list/abstract-contact-list.component';
import { AbstractNotifiableEntityContactListComponent } from './abstract-entity-contact-list/abstract-notifiable-entity-contact-list.component';
import { BpSelectableContactListComponent } from './bp-selectable-contact-list/bp-selectable-contact-list.component';
import { BpSelectableContactCardComponent } from './bp-selectable-contact-list/bp-selectable-contact-card/bp-selectable-contact-card.component';
import { BpSubmitableContactListComponent } from './bp-submitable-contact-list/bp-submitable-contact-list.component';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { AbstractNotifiableEntityContactListUpdatePopupComponent } from './abstract-notifiable-entity-contact-list-update-popup/abstract-notifiable-entity-contact-list-update-popup.component';
import { WipMessageComponent } from './wip-message/wip-message.component';
import { InfoMessageComponent } from './info-message/info-message.component';
import { CustomContactListValidator } from './validators/custom-contact-list.validator';
import { CustomInvoiceExchangeRateValidator } from './validators/custom-invoice-exchange-rate.validator';
import { InputNumber, InputNumberModule } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormInlineErrorDirective } from '@irian-ro/shared-ui';
import { InvoiceOrderValidator } from './validators/invoice-order.validator';
import { ChipModule } from 'primeng/chip';
import { InputTrimDirective } from '@well/infra/directives';
import { EmployeeSelectionAvatarComponent } from './employee-selection-avatar/employee-selection-avatar.component';
import { EmployeeSelectionOverlayPanelComponent } from './employee-selection-overlay-panel/employee-selection-overlay-panel.component';
import { FloatLabel } from 'primeng/floatlabel';
import { DatePicker } from 'primeng/datepicker';
import { Select, SelectModule } from 'primeng/select';
import { Button, ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';

@NgModule({
  imports: [
    MessageModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    RouterModule,
    TranslateModule,
    SelectModule,
    InputNumber,
    DatePicker,
    TooltipModule,
    Popover,
    AutoCompleteModule,
    DateUtilsModule,
    DialogModule,
    IriroSharedComponentsModule,
    MessagesModule,
    AvatarModule,
    AvatarGroupModule,
    StepsModule,
    RippleModule,
    BadgeModule,
    FileStorageModule,
    ResourceDropdownModule,
    ListboxModule,
    AccordionModule,
    CheckboxModule,
    InputNumberModule,
    MultiSelectModule,
    InputNumberModule,
    FormInlineErrorDirective,
    ChipModule,
    InputTrimDirective,
    EmployeeSelectionOverlayPanelComponent,
    EmployeeSelectionOverlayPanelComponent,
    TruncateSeriesNumberPipe,
    WellDatePipe,
    FloatLabel,
    Popover,
    DatePicker,
    Select,
    Button,
  ],
  declarations: [
    EmptyComponent,
    OverviewPanelComponent,
    CollapsibleSidenavComponent,
    CustomContactListValidator,
    CustomInvoiceExchangeRateValidator,
    InvoiceOrderValidator,
    CountryCodeToTimezonePipe,
    MenuItemHasAnyRightsPipe,
    SaveAsPdfComponent,
    WizardComponent,
    WizardTabComponent,
    EmptyListPlaceholderComponent,
    PageTitleComponent,
    FormControlErrorsComponent,
    LinkComponent,
    TimeAvailabilityComponent,
    AvailabilityTimerComponent,
    DateTimeDropdownPickerComponent,
    PlaceAutocompleteFloatingLabelComponent,
    DisplayTimeWindowComponent,
    SelectTimeWindowComponent,
    SidebarMenuComponent,
    MenuItemComponent,
    MenuItemContentComponent,
    TooltipDropdownComponent,
    ResourceInputComponent,
    TimeUntilDeadlinePipe,
    FlagLanguageDropdownComponent,
    TermsAndConditionsPreviewDialogComponent,
    CompanyDetailsComponent,
    WizardTabFooterDirective,
    MenuUserProfileComponent,
    ListFilterComponent,
    DisplayNullValuePipe,
    StatusTagComponent,
    SuggestionMessageComponent,
    TimeTableFilterComponent,
    StepWizardDialogComponent,
    StepWizardTabComponent,
    AlertNotificationCardComponent,
    IsTimeAfterNowPipe,
    CollapsiblePanelComponent,
    CollapsibleTextComponent,
    WellStringToDatePipe,
    CountryCodeToLabelPipe,
    ResourceSavedNavigationDialogComponent,
    TranslateMenuItemsPipe,
    ValidToFilterComponent,
    SelectableCardComponent,
    ValidToFilterComponent,
    HorizontalScrollComponent,
    FormatVolumePipe,
    AccountFieldComponent,
    AccountDialogComponent,
    AccountDropdownComponent,
    EmployeeSelectionAvatarComponent,
    GetFirstThreeChrPipe,
    LanguageDialogComponent,
    RippleComponent,
    PreventScrollOnRouterOutletDirective,
    WarningTextComponent,
    GlobalFilterComponent,
    DistanceAsyncValidator,
    ViewOnlyDirective,
    PersonCircleComponent,
    AbstractContactListComponent,
    AbstractNotifiableEntityContactListComponent,
    AbstractNotifiableEntityContactListUpdatePopupComponent,
    BpSelectableContactListComponent,
    BpSelectableContactCardComponent,
    BpSubmitableContactListComponent,
    WipMessageComponent,
    InfoMessageComponent,
  ],
  exports: [
    EmptyComponent,
    OverviewPanelComponent,
    CollapsibleSidenavComponent,
    CountryCodeToTimezonePipe,
    SaveAsPdfComponent,
    CustomContactListValidator,
    CustomInvoiceExchangeRateValidator,
    InvoiceOrderValidator,
    MenuItemHasAnyRightsPipe,
    WizardComponent,
    WizardTabComponent,
    EmptyListPlaceholderComponent,
    PageTitleComponent,
    FormControlErrorsComponent,
    LinkComponent,
    TimeAvailabilityComponent,
    AvailabilityTimerComponent,
    DateTimeDropdownPickerComponent,
    PlaceAutocompleteFloatingLabelComponent,
    DisplayTimeWindowComponent,
    SelectTimeWindowComponent,
    WellDatePipe,
    SidebarMenuComponent,
    TooltipDropdownComponent,
    ResourceInputComponent,
    TimeUntilDeadlinePipe,
    FlagLanguageDropdownComponent,
    TermsAndConditionsPreviewDialogComponent,
    CompanyDetailsComponent,
    WizardTabFooterDirective,
    ListFilterComponent,
    DisplayNullValuePipe,
    StatusTagComponent,
    SuggestionMessageComponent,
    TimeTableFilterComponent,
    StepWizardDialogComponent,
    StepWizardTabComponent,
    AlertNotificationCardComponent,
    IsTimeAfterNowPipe,
    CollapsiblePanelComponent,
    CollapsibleTextComponent,
    WellStringToDatePipe,
    CountryCodeToLabelPipe,
    ResourceSavedNavigationDialogComponent,
    TranslateMenuItemsPipe,
    ValidToFilterComponent,
    TruncateSeriesNumberPipe,
    ValidToFilterComponent,
    SelectableCardComponent,
    HorizontalScrollComponent,
    FormatVolumePipe,
    AccountFieldComponent,
    AccountDialogComponent,
    AccountDropdownComponent,
    EmployeeSelectionAvatarComponent,
    GetFirstThreeChrPipe,
    LanguageDialogComponent,
    RippleComponent,
    PreventScrollOnRouterOutletDirective,
    WarningTextComponent,
    GlobalFilterComponent,
    DistanceAsyncValidator,
    ViewOnlyDirective,
    PersonCircleComponent,
    AbstractContactListComponent,
    AbstractNotifiableEntityContactListComponent,
    BpSelectableContactListComponent,
    BpSelectableContactCardComponent,
    BpSubmitableContactListComponent,
    AbstractNotifiableEntityContactListUpdatePopupComponent,
    WipMessageComponent,
    InfoMessageComponent,
  ],
})
export class SharedComponentsModule {}
